<template>
  <form @submit.prevent="submitHandler">
    <div class="flex items-end gap-2 flex-1">
      <CountrySelect
        @click="$track(ImtSearchFormFieldOpen('country_from'))"
        class="flex-auto"
        :label="$t('transfer_search.send_money_from')"
        selection-class="px-3 py-5 mt-1 bg-gray-100 rounded-8"
        selection-text-class="text-16 font-bold md:w-48"
        :header-text="$t('transfer_search.sending_country')"
        no-arrow
        @update:model-value="selectCountryFrom"
        v-model="countryFrom"
      />
      <CurrencySelect
        @click="$track(ImtSearchFormFieldOpen('currency_from'))"
        class="flex-none w-20"
        selection-class="px-4 py-5 bg-gray-100 rounded-8 justify-center"
        selection-text-class="text-16 font-bold"
        :header-text="$t('transfer_search.sending_currency')"
        :currencies="fromCurrencies"
        v-model="currencyFrom"
        no-arrow
      />
    </div>
    <div class="flex items-end gap-2 flex-1 mt-5 md:mt-0">
      <CountrySelect
        @click="$track(ImtSearchFormFieldOpen('country_to'))"
        class="flex-auto"
        :label="$t('transfer_search.to')"
        selection-class="px-3 py-5 mt-1 bg-gray-100 rounded-8"
        selection-text-class="text-16 font-bold md:w-48"
        :header-text="$t('transfer_search.receiving_country')"
        no-arrow
        @update:model-value="selectCountryTo"
        v-model="countryTo"
      />
      <CurrencySelect
        @click="$track(ImtSearchFormFieldOpen('currency_to'))"
        class="flex-none w-20"
        selection-class="px-4 py-5 bg-gray-100 rounded-8 justify-center"
        selection-text-class="text-16 font-bold"
        :header-text="$t('transfer_search.receiving_currency')"
        :currencies="toCurrencies"
        v-model="currencyTo"
        no-arrow
      />
    </div>
    <LazyPayoutSelect
      v-if="IS_MOBILE_APP"
      @click="$track(ImtSearchFormFieldOpen('payout'))"
      class="mt-5"
      :corridor="{
        currencyFrom,
        currencyTo,
        countryTo,
        countryFrom,
      }"
      :header="$t('transfer_search.title')"
      :label="$t('transfer_search.select_receive_method')"
      selection-class="px-3 py-4 mt-1 bg-gray-100 rounded-8"
      selection-text-class="text-16 font-bold"
      no-arrow
      v-model="selectedPayout"
    />
    <AmountInput
      class="mt-5 xl:mt-0"
      :header-text="$t('transfer_search.sending_currency')"
      :amount-value="amount"
      :amount-placeholder="$t('transfer_search.enter_an_amount')"
      :currency-value="currencyFrom"
      :custom-error="$t('transfer_search.amount_is_required')"
      @input-amount="amount = $event"
    />
    <button
      ref="submitBtn"
      type="submit"
      class="btn btn-amber text-17 items-center flex justify-center h-16 self-end mb-0.5 w-full mt-5 xl:mt-0 xl:w-auto"
      :disabled="isLoading"
    >
      <span class="xl:hidden">{{ cta || $t('compare') }}</span>
      <MSpinner
        v-if="isLoading"
        class="ml-2 xl:ml-0"
        size="h-6 w-6"
        stroke-width="3"
        color="text-white"
      />
      <svg role="presentation" focusable="false" tabindex="-1" v-else
        class="icon icon-md hidden xl:inline"
         
        alt="compare"
        svg-inline
       xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><title>compare</title>
  <path fill="currentColor" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
</svg>
    </button>
  </form>
</template>
<script lang="ts" setup>
import { SearchSubmit } from '@core/analytics/core-events/moneyTransfer'
import AmountInput from './amount-input.vue'
import { PageTypes } from '@core/constants/pages'
import { OnboardingScreenView } from '@core/analytics/core-events/onboarding'
import { ImtSearchFormFieldOpen } from '@core/analytics/core-events/app'

const IS_MOBILE_APP = __IS_MOBILE_APP__

const props = defineProps<{
  defaultCountryFrom?: string
  defaultCountryTo?: string
  defaultAmount?: number | null
  defaultPayout?: string
  cta?: string | null
  sidebar?: boolean
  noOverlay?: boolean
  onboarding?: boolean
}>()

const emit = defineEmits(['submit'])

const { $track, $masterData, $cookie, $inAppEvent } = useNuxtApp()
const route = useRoute()

const countryFrom = ref<string | null>(props.defaultCountryFrom ?? null)
const countryTo = ref<string | null>(props.defaultCountryTo ?? null)
const currencyFrom = ref<string>('')
const currencyTo = ref<string>('')
const selectedPayout = ref<string>(props.defaultPayout ?? 'bank')
const amount = ref<number>(props.defaultAmount ?? 0)

if (route.meta.pageType === PageTypes.TRANSFER) {
  countryFrom.value = countryFromParam(
    route.params.countryFrom,
    $masterData.countries,
    $masterData.countrySlugIsoMap,
  ).iso
  countryTo.value = countryFromParam(
    route.params.countryTo,
    $masterData.countries,
    $masterData.countrySlugIsoMap,
  ).iso

  currencyFrom.value = String(route.params.currencyFrom).toUpperCase()
  currencyTo.value = String(route.params.currencyTo).toUpperCase()

  if (route.params.amount) {
    amount.value = Number(route.params.amount)
  }

  if (route.query.tab) {
    selectedPayout.value = String(route.query.tab)
  }
}

if (route.meta.pageType === PageTypes.CURRENCY) {
  const currFrom = currencyFromParam(route.params.currencyFrom, $masterData.currencies)
  const currTo = currencyFromParam(route.params.currencyTo, $masterData.currencies)
  if (currFrom?.defaultCountry && currTo?.defaultCountry) {
    countryFrom.value = currFrom.defaultCountry.iso
    countryTo.value = currTo.defaultCountry.iso
    currencyFrom.value = currFrom.iso
    currencyTo.value = currTo.iso
  }
}

onMounted(async () => {
  if (props.onboarding) {
    await $track(OnboardingScreenView({ screen: 'imt_search_form' }))
  }
  if (!countryFrom.value && !countryTo.value) {
    const lastComparison = await getLastComparisonInfo()
    if (lastComparison) {
      countryFrom.value = lastComparison.countryFrom
      countryTo.value = lastComparison.countryTo
      currencyFrom.value = lastComparison.currencyFrom
      currencyTo.value = lastComparison.currencyTo
      selectedPayout.value = lastComparison.payout || ''
      amount.value = lastComparison.amount
    }
  }

  if (!countryFrom.value) {
    countryFrom.value = ($cookie.value.getGeoLoc() ?? 'CH').toUpperCase()
  }
  if (!countryTo.value) {
    countryTo.value = getCountry(countryFrom.value)?.defaultCountryTo.iso ?? null
  }

  if (!currencyFrom.value) {
    currencyFrom.value = getCountry(countryFrom.value)?.defaultCurrency.iso || ''
  }
  if (!currencyTo.value) {
    currencyTo.value = getCountry(countryTo.value)?.defaultCurrency.iso || ''
  }
})

function getCountry(country: string | null) {
  return $masterData.countries.get(country ?? '')
}

function getCurrencyLabel(iso: string) {
  return $masterData.currencies.get(iso)?.labels.singular
}

const fromCurrencies = computed(() => {
  const curr = getCountry(countryFrom.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

const toCurrencies = computed(() => {
  const curr = getCountry(countryTo.value)?.currencies ?? []
  return curr.map(({ iso }) => ({ iso, label: getCurrencyLabel(iso) ?? iso }))
})

function selectCountryFrom(country: string) {
  currencyFrom.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

function selectCountryTo(country: string) {
  currencyTo.value = getCountry(country)?.defaultCurrency.iso ?? ''
}

const isLoading = ref(false)
async function submitHandler() {
  if (
    !countryFrom.value ||
    !countryTo.value ||
    !currencyFrom.value ||
    !currencyTo.value ||
    !amount.value
  ) {
    return
  }
  const foundCountryFrom = getCountry(countryFrom.value)
  const foundCountryTo = getCountry(countryTo.value)
  if (!foundCountryFrom || !foundCountryTo) {
    return
  }

  emit('submit')
  isLoading.value = true
  if (!__IS_MOBILE_APP__) {
    setTimeout(() => {
      isLoading.value = false
    }, 5000)
  }
  await $track(
    SearchSubmit({
      type: 'Form',
      countryFrom: countryFrom.value,
      countryTo: countryTo.value,
      currencyFrom: currencyFrom.value,
      currencyTo: currencyTo.value,
      payout: selectedPayout.value,
      amount: amount.value,
    }),
  )

  if (__IS_MOBILE_APP__) {
    $inAppEvent('af_imt_comparison')
  }

  const transferUrl = getIMTLiveUrl({
    countryFrom: { iso: countryFrom.value },
    countryTo: { iso: countryTo.value },
    currencyFrom: { iso: currencyFrom.value },
    currencyTo: { iso: currencyTo.value },
    amount: amount.value,
    payout: selectedPayout.value,
  })

  await storeLastComparisonInfo({
    countryFrom: countryFrom.value,
    countryTo: countryTo.value,
    currencyFrom: currencyFrom.value,
    currencyTo: currencyTo.value,
    amount: amount.value,
    payout: selectedPayout.value,
    cheapestProvider: null,
    spotlightedProvider: null,
    liveComparison: true,
    midMarketRate: 0,
    timestamp: 0,
  })

  if (__IS_MOBILE_APP__) {
    if (route.name === 'dashboard') {
      await useRouter().push({
        name: 'dashboard',
        query: {
          updatedAt: Date.now().toString(),
        },
      })
    } else {
      await navigate(transferUrl, { replace: props.onboarding })
    }
  } else {
    await navigate(transferUrl)
  }
}
</script>
